import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../../lib/api.js';

/**
 * Settings → Your agents: the one way to bring your own AI. Your agent
 * (Claude Code, Cursor, Codex, anything that speaks MCP) connects into
 * Prism Engine Cloud with a token and gets Prism Engine Cloud's project tools; Prism Engine Cloud itself
 * only runs Prisma.
 */

interface Connection {
  id: number;
  name: string;
  projectId: number | null;
  createdAt: string;
  lastUsedAt: string | null;
}

function Copy({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="ghost tiny"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setDone(true);
          window.setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? 'Copied' : label}
    </button>
  );
}

export function AgentsSettings() {
  const [url, setUrl] = useState('');
  const [connections, setConnections] = useState<Connection[]>([]);
  const [projects, setProjects] = useState<{ id: number; name: string }[]>([]);
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState<number | null>(null);
  const [fresh, setFresh] = useState<{ token: string; url: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ url: string; connections: Connection[] }>('/agent-connections');
      setUrl(r.url);
      setConnections(r.connections);
      setProjects((await api.get<{ projects: { id: number; name: string }[] }>('/projects')).projects);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load your agents.');
    }
  }, []);

  useEffect(() => void load(), [load]);

  async function connect(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      const r = await api.post<{ token: string; url: string }>('/agent-connections', { name: name.trim(), projectId });
      setFresh({ ...r, name: name.trim() });
      setName('');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not make the connection.');
    }
  }

  async function remove(c: Connection) {
    if (!window.confirm(`Disconnect "${c.name}"? Its token stops working right away.`)) return;
    await api.del(`/agent-connections/${c.id}`).catch(() => undefined);
    await load();
  }

  const claudeCmd = fresh ? `claude mcp add --transport http prismcore ${fresh.url} --header "Authorization: Bearer ${fresh.token}"` : '';
  const json = fresh
    ? JSON.stringify({ mcpServers: { prismcore: { type: 'http', url: fresh.url, headers: { Authorization: `Bearer ${fresh.token}` } } } }, null, 2)
    : '';
  const projectName = (id: number | null) => (id === null ? 'All my projects' : (projects.find((p) => p.id === id)?.name ?? `Project ${id}`));

  return (
    <div className="agents-settings">
      <h2>Your agents</h2>
      <p className="muted">
        Prism Engine Cloud's AI is Prisma. If you also use your own AI agent (Claude Code, Cursor, Codex or anything that speaks MCP), connect it here: it can read and change your projects, check
        your game, read the engine manual, plan on the board and ask Prisma for help.
      </p>

      <form className="agents-form" onSubmit={connect}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name it, e.g. My Claude Code" maxLength={60} required />
        <select value={projectId ?? ''} onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : null)} aria-label="Which projects">
          <option value="">All my projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <button type="submit" className="primary" disabled={!name.trim()}>
          Connect an agent
        </button>
      </form>
      {error && <p className="login-error">{error}</p>}

      {fresh && (
        <div className="agents-fresh">
          <p>
            <strong>{fresh.name} is ready.</strong> Copy its token now: it is shown only once.
          </p>
          <div className="agents-line">
            <code>{fresh.token}</code>
            <Copy text={fresh.token} label="Copy token" />
          </div>
          <p className="muted small">Claude Code:</p>
          <div className="agents-line">
            <code>{claudeCmd}</code>
            <Copy text={claudeCmd} />
          </div>
          <p className="muted small">Cursor, Windsurf and other MCP clients (mcp.json):</p>
          <pre className="agents-json">{json}</pre>
          <Copy text={json} label="Copy JSON" />
          <button type="button" className="ghost tiny" onClick={() => setFresh(null)}>
            Done
          </button>
        </div>
      )}

      <h3>Connected</h3>
      {connections.length === 0 ? (
        <p className="muted">No agents connected yet.</p>
      ) : (
        <ul className="agents-list">
          {connections.map((c) => (
            <li key={c.id}>
              <span>
                <strong>{c.name}</strong>
                <span className="muted small">
                  {' '}
                  · {projectName(c.projectId)} · {c.lastUsedAt ? `last used ${new Date(c.lastUsedAt).toLocaleString()}` : 'not used yet'}
                </span>
              </span>
              <button type="button" className="ghost danger tiny" onClick={() => void remove(c)}>
                Disconnect
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="muted small">
        Address: <code>{url}</code>
        {url.startsWith('http://') ? ' (works while the Prism Engine Cloud app is open on this PC)' : ''}
      </p>
    </div>
  );
}
